import { useState } from 'react';
import {
  CreditCard,
  Banknote,
  Gift,
  Loader2,
  DollarSign,
  CheckCircle2,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';

type PaymentMethod = 'cash' | 'card' | 'gift_card' | 'other';

interface Order {
  id: string;
  order_number: string;
  total: number;
  status: string;
}

interface AddPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | null;
  onAddPayment: (data: {
    orderId: string;
    paymentMethod: PaymentMethod;
    amount: number;
    tipAmount?: number;
  }) => Promise<void>;
  isProcessing?: boolean;
}

const paymentMethods: Array<{
  id: PaymentMethod;
  label: string;
  icon: typeof CreditCard;
  color: string;
}> = [
  { id: 'cash', label: 'Cash', icon: Banknote, color: 'bg-green-500/10 text-green-600 border-green-200 hover:bg-green-500/20' },
  { id: 'card', label: 'Card', icon: CreditCard, color: 'bg-blue-500/10 text-blue-600 border-blue-200 hover:bg-blue-500/20' },
  { id: 'gift_card', label: 'Gift Card', icon: Gift, color: 'bg-purple-500/10 text-purple-600 border-purple-200 hover:bg-purple-500/20' },
  { id: 'other', label: 'Other', icon: DollarSign, color: 'bg-gray-500/10 text-gray-600 border-gray-200 hover:bg-gray-500/20' },
];

export function AddPaymentDialog({
  open,
  onOpenChange,
  order,
  onAddPayment,
  isProcessing = false,
}: AddPaymentDialogProps) {
  const { formatCurrency } = useCurrency();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('cash');
  const [amount, setAmount] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      // Reset state when opening
      setSelectedMethod('cash');
      setAmount(order?.total.toFixed(2) || '');
      setTipAmount('');
      setError(null);
    }
    onOpenChange(isOpen);
  };

  const handleSubmit = async () => {
    if (!order) return;

    const parsedAmount = parseFloat(amount);
    const parsedTip = tipAmount ? parseFloat(tipAmount) : 0;

    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (parsedTip < 0) {
      setError('Tip cannot be negative');
      return;
    }

    setError(null);

    try {
      await onAddPayment({
        orderId: order.id,
        paymentMethod: selectedMethod,
        amount: parsedAmount,
        tipAmount: parsedTip > 0 ? parsedTip : undefined,
      });
      handleOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to process payment');
    }
  };

  if (!order) return null;

  const parsedAmount = parseFloat(amount) || 0;
  const parsedTip = parseFloat(tipAmount) || 0;
  const totalPayment = parsedAmount + parsedTip;

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpen}>
      <ResponsiveModalContent maxWidth="md">
        <ResponsiveModalHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div>
              <ResponsiveModalTitle>Add Payment</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Order #{order.order_number} • Total: {formatCurrency(order.total)}
              </ResponsiveModalDescription>
            </div>
          </div>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-6">
          {/* Payment Method Selection */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Payment Method</Label>
            <div className="grid grid-cols-2 gap-3">
              {paymentMethods.map((method) => {
                const Icon = method.icon;
                const isSelected = selectedMethod === method.id;
                return (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setSelectedMethod(method.id)}
                    className={cn(
                      'flex items-center gap-3 p-4 rounded-lg border-2 transition-all',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : `border-transparent ${method.color}`
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{method.label}</span>
                    {isSelected && (
                      <CheckCircle2 className="h-4 w-4 ml-auto text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount Input */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="payment-amount" className="text-sm font-medium">
                Payment Amount
              </Label>
              <div className="relative mt-1.5">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="payment-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-9 text-lg font-semibold"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="tip-amount" className="text-sm font-medium">
                Tip (Optional)
              </Label>
              <div className="relative mt-1.5">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="tip-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="pl-9"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order Total</span>
              <span>{formatCurrency(order.total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment Amount</span>
              <span>{formatCurrency(parsedAmount)}</span>
            </div>
            {parsedTip > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tip</span>
                <span>{formatCurrency(parsedTip)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold pt-2 border-t">
              <span>Total Payment</span>
              <span>{formatCurrency(totalPayment)}</span>
            </div>
            {parsedAmount < order.total && (
              <p className="text-xs text-amber-600 mt-2">
                Note: Payment is less than order total. Order will remain unpaid.
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg">
              {error}
            </p>
          )}
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button
            variant="outline"
            onClick={() => handleOpen(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="blaze"
            onClick={handleSubmit}
            disabled={isProcessing || parsedAmount <= 0}
            className="gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Process Payment
              </>
            )}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

